import {
    Component,
    inject,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
} from '@angular/core';
import { CardModule, DirectivesModule } from 'rcp-common-lib';
import { AgCharts } from 'ag-charts-angular';
import { AgChartOptions, AgBarSeriesOptions } from 'ag-charts-types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    AgingTrendMetricRequest,
    ARSnapshotResponse,
} from '@core/generated/openapi';
import { AuthService } from '@services/auth/auth.service';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { formatCurrency } from '@angular/common';
import { ArSnapshotService } from '@services/ar-snapshot/ar-snapshot.service';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { skip } from 'rxjs';

interface ChartSettings {
    chartData: any[];
    series: AgBarSeriesOptions[];
}

interface ChartDataRange {
    range: string;
}

@UntilDestroy()
@Component({
    selector: 'rcp-oup-aging-trend-detail-metric',
    imports: [CardModule, DirectivesModule, AgCharts],
    templateUrl: './aging-trend-detail-metric.component.html',
    styleUrl: './aging-trend-detail-metric.component.scss',
})
export class AgingTrendDetailMetricComponent implements OnInit {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    @Input() claimStalledFilterId: string;

    isLoading: boolean = false;
    chartOptions: AgChartOptions;
    locations: number[] = [];
    fillColors = ['#34BFE1', '#E1CC00', '#5090DC', '#FFA03A', '#459D55'];
    fillColorAssignments: Map<string, string> = new Map();

    constructor(
        private authService: AuthService,
        private arSnapshotService: ArSnapshotService,
        @Inject(LOCALE_ID) private locale: string,
    ) {}

    ngOnInit() {
        this.getSelectedLocations();
        this.onStalledFilterChange();
    }

    getSelectedLocations() {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.locations = [];
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    !locations.every((val) => val === undefined)
                ) {
                    this.locations = locations;
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    locations.every((val) => val === undefined)
                ) {
                    this.loadData();
                }
            });
    }

    onStalledFilterChange() {
        if (this.claimStalledFilterId) {
            this.claimStalledFilterService
                .onFilterStatusChange(this.claimStalledFilterId)
                .pipe(untilDestroyed(this), skip(1))
                .subscribe({
                    next: () => this.loadData(),
                });
        }
    }

    loadData() {
        this.isLoading = true;
        this.arSnapshotService
            .findARSnapshotAgingTrendMetrics(this.getReportRequest())
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.createChart(this.setupData(data));
                this.isLoading = false;
            });
    }

    getReportRequest(): AgingTrendMetricRequest {
        return {
            accountLocationIds: this.locations,
            dateRange: {
                minDate: dayjs()
                    .subtract(3, 'months')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                maxDate: dayjs()
                    .subtract(1, 'month')
                    .endOf('month')
                    .format('YYYY-MM-DD'),
            },
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    this.claimStalledFilterId,
                ),
        };
    }

    setupData(data: ARSnapshotResponse[]): ChartSettings {
        let chartDataRanges: ChartDataRange[] = [
            { range: 'age61To90' },
            { range: 'age91To120' },
            { range: 'age120Plus' },
        ];
        let chartSettings: ChartSettings = {
            chartData: [],
            series: [],
        };
        let seriesDates: string[] = [];

        chartSettings.chartData = chartDataRanges.map((chartDataRange) => {
            return this.createChartDataItem(data, chartDataRange);
        });

        chartSettings.series = data.map((metric) => {
            if (
                (metric.age61To90 === 0 &&
                    metric.age91To120 === 0 &&
                    metric.age120Plus === 0) ||
                seriesDates.includes(metric.snapshotDate)
            ) {
                return undefined;
            } else {
                seriesDates.push(metric.snapshotDate);
                return this.createChartSeries(metric.snapshotDate);
            }
        });

        chartSettings.series = chartSettings.series.filter((value) => {
            return !isNil(value);
        });

        return chartSettings;
    }

    createChartDataItem(
        data: ARSnapshotResponse[],
        chartDataRange: ChartDataRange,
    ) {
        let chartItem = {
            range: chartDataRange.range,
        };

        // Create month values with keys like: 20240731: 25 for each range
        data.forEach((metric) => {
            const keyName = metric.snapshotDate.replace(/-/g, '');

            // See if we've already defined this keyName. If so, add to it.
            if (keyName in chartItem) {
                // @ts-ignore
                chartItem[keyName] =
                    // @ts-ignore
                    chartItem[keyName] + metric[chartDataRange.range];
            } else {
                // @ts-ignore
                chartItem[keyName] = metric[chartDataRange.range];
            }
        });

        return chartItem;
    }

    createChartSeries(date: string) {
        return {
            type: 'bar',
            xKey: 'range',
            yKey: date.replace(/-/g, ''),
            yName: dayjs(date).format('MMM'),
            tooltip: {
                renderer: ({ datum, xKey, yKey }) => {
                    return {
                        title: formatCurrency(datum[yKey], this.locale, '$'),
                        heading: `${dayjs(date).format('MMM')} (${this.getRangeLabel(datum[xKey])})`,
                        data: [],
                    };
                },
            },
            fill: this.getFillColor(date),
        } as AgBarSeriesOptions;
    }

    createChart(data: ChartSettings) {
        this.chartOptions = {
            axes: [
                {
                    type: 'category',
                    position: 'bottom',
                    keys: ['range'],
                    label: {
                        formatter: ({ value }) => {
                            return this.getRangeLabel(value);
                        },
                    },
                },
                {
                    type: 'number',
                    position: 'left',
                    label: {
                        formatter: ({ value }) => {
                            return this.formatCurrency(value);
                        },
                    },
                    interval: {
                        minSpacing: 20,
                        maxSpacing: 25,
                    },
                },
            ],
            data: data.chartData,
            legend: {
                position: 'bottom',
            },
            series: data.series,
        };
    }

    getRangeLabel(value: string) {
        switch (value) {
            case 'age61To90':
                return '61-90';
            case 'age91To120':
                return '91-120';
            case 'age120Plus':
                return '121+';
            default:
                return value;
        }
    }

    formatCurrency(value: number) {
        const currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: 'compact',
        });

        return currencyFormatter.format(value);
    }

    getFillColor(date: string) {
        // Need to see if we have a saved color for given date, if so use that.
        // If not we need to grab a new color and assign it.
        if (isNil(this.fillColorAssignments.get(date))) {
            this.fillColorAssignments.set(date, this.fillColors.pop());
        }

        return this.fillColorAssignments.get(date);
    }
}
